import React, { useState, useEffect } from 'react';
import api from '@/auth/api';
import Loader from '@/components/Loader';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title
} from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faTable,
  faUndo,
  faFilter,
  faCalendarAlt,
  faCoins,
  faUsers,
  faFileAlt,
  faRefresh,
  faClock,
  faLayerGroup,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

// Register Chart.js components
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title
);

function SurveyStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter States
  const [filter, setFilter] = useState('this_month'); // Default to this month
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [projectCodeFilter, setProjectCodeFilter] = useState('');
  const [includeSegments, setIncludeSegments] = useState(false);

  // Pagination States
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Store all discovered project codes for the filter dropdown
  const [projectCodes, setProjectCodes] = useState([]);

  // UI View Mode: 'charts' | 'table'
  const [viewMode, setViewMode] = useState('charts');

  // Fetch Survey Stats data from API
  const fetchSurveyStats = async (pageNumber = page, size = pageSize, resetFilter = null, resetIncludeSegments = null) => {
    setLoading(true);
    setError(null);
    try {
      const activeFilter = resetFilter !== null ? resetFilter : filter;
      const activeIncludeSegments = resetIncludeSegments !== null ? resetIncludeSegments : includeSegments;
      const params = {
        page: pageNumber,
        page_size: size,
        include_segments: activeIncludeSegments ? 'true' : 'false'
      };

      if (activeFilter) {
        params.filter = activeFilter;
      }

      if (activeFilter === 'custom') {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }

      if (projectCodeFilter) {
        params.project_code = projectCodeFilter;
      }

      const response = await api.get('/transaction/panel/transaction/stats/survey', { params });

      if (response.data) {
        setData(response.data);
        
        // Dynamically update available project codes list
        if (response.data.project_codes && response.data.project_codes.length > 0) {
          // If we haven't selected a specific project code filter, or projectCodes is empty, update the cache.
          if (!projectCodeFilter || projectCodes.length === 0) {
            // Merge or set to ensure we keep a clean list of all unique codes
            setProjectCodes(prev => {
              const merged = new Set([...prev, ...response.data.project_codes]);
              return Array.from(merged).sort();
            });
          }
        }
      } else {
        setError('Empty response received from the server.');
      }
    } catch (err) {
      console.error('Error fetching survey stats:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load survey statistics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurveyStats(page, pageSize);
  }, [page, pageSize]);

  // Handle Form Filters Submission
  const handleApplyFilter = (e) => {
    e.preventDefault();
    setPage(1); // Reset to page 1 on filter application
    fetchSurveyStats(1, pageSize);
  };

  // Reset all filters to defaults
  const handleResetFilter = () => {
    setFilter('this_month');
    setStartDate('');
    setEndDate('');
    setProjectCodeFilter('');
    setIncludeSegments(false);
    setPage(1);
    fetchSurveyStats(1, pageSize, 'this_month', false);
  };

  // Number Formatting Helpers
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return new Intl.NumberFormat().format(num);
  };

  const formatPoints = (points) => {
    if (points === undefined || points === null) return '0 pts';
    return formatNumber(points) + ' pts';
  };

  if (loading && !data) {
    return (
      <div className="w-full py-24 flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="w-full max-w-4xl mx-auto mt-8 p-6 bg-red-50 border border-red-200 rounded-xl text-center dark:bg-red-950/20 dark:border-red-900/30 animate-pulse">
        <p className="text-red-700 dark:text-red-400 font-semibold mb-3">Error Loading Survey Stats</p>
        <p className="text-sm text-red-600 dark:text-red-500 mb-4">{error}</p>
        <button
          onClick={() => fetchSurveyStats(page, pageSize)}
          className="activeBtn px-4 py-2 text-white rounded-lg text-sm hover:opacity-90 transition flex items-center justify-center mx-auto"
        >
          <FontAwesomeIcon icon={faRefresh} className="mr-2 animate-spin-slow" />
          Retry
        </button>
      </div>
    );
  }

  const { Survey = [], ProjectPointSegments = [], pagination = {} } = data || {};

  // Compute stats metrics based on current Survey page content
  const totalPointsPage = Survey.reduce((acc, curr) => acc + (curr.total_points || 0), 0);
  const totalUserCountPage = Survey.reduce((acc, curr) => acc + (curr.user_count || 0), 0);
  const totalSurveys = pagination.count || Survey.length;

  // Chart configs
  const chartLabels = Survey.map(item => item.project_code);
  const chartPointsData = Survey.map(item => item.total_points || 0);
  const chartUsersData = Survey.map(item => item.user_count || 0);

  const pointsChartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Points Distributed',
      data: chartPointsData,
      backgroundColor: 'rgba(35, 98, 159, 0.85)',
      borderColor: 'rgba(35, 98, 159, 1)',
      borderWidth: 1.5,
      borderRadius: 6,
    }]
  };

  const usersChartData = {
    labels: chartLabels,
    datasets: [{
      label: 'Completed Users',
      data: chartUsersData,
      backgroundColor: 'rgba(103, 169, 123, 0.85)',
      borderColor: 'rgba(103, 169, 123, 1)',
      borderWidth: 1.5,
      borderRadius: 6,
    }]
  };

  const chartOptions = (labelType) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: 'currentColor',
          font: { family: 'Inter, system-ui', size: 12 }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: 'rgba(156, 163, 175, 0.2)',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context) => {
            let label = context.dataset.label || '';
            if (label) label += ': ';
            if (context.parsed.y !== undefined) {
              label += labelType === 'points' ? formatPoints(context.parsed.y) : formatNumber(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: 'currentColor',
          callback: (value) => labelType === 'points' ? formatPoints(value) : value,
          font: { size: 10 }
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)'
        }
      },
      x: {
        ticks: {
          color: 'currentColor',
          font: { size: 10 },
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          display: false
        }
      }
    }
  });

  // Pagination navigation helpers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination.no_page || 1)) {
      setPage(newPage);
    }
  };

  // Safely renders the schema keys of ProjectPointSegments dynamically
  const renderSegmentsTable = () => {
    if (!ProjectPointSegments || ProjectPointSegments.length === 0) {
      return (
        <div className="text-center py-6 text-gray-400 dark:text-gray-500 text-sm">
          No point segments found matching current filters. Ensure 'Include Segments' is checked.
        </div>
      );
    }

    // Identify all keys in the objects except system columns if any
    const sample = ProjectPointSegments[0];
    const columns = Object.keys(sample);

    return (
      <div className="w-full overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 text-xs font-semibold uppercase tracking-wider">
              {columns.map(col => (
                <th key={col} className="py-3 px-4 capitalize">
                  {col.replace(/_/g, ' ')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {ProjectPointSegments.map((item, idx) => (
              <tr key={idx} className="hover:bg-gray-55/40 dark:hover:bg-gray-700/30 transition">
                {columns.map(col => {
                  const val = item[col];
                  const isPoints = col.toLowerCase().includes('point');
                  const isCount = col.toLowerCase().includes('count') || col.toLowerCase().includes('user');
                  return (
                    <td key={col} className="py-3 px-4 font-medium">
                      {isPoints ? (
                        <span className="font-semibold text-blue-500">{formatPoints(val)}</span>
                      ) : isCount ? (
                        formatNumber(val)
                      ) : (
                        String(val ?? '-')
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="w-full max-w-[95%] mx-auto px-4 py-6 text-gray-800 dark:text-gray-200">
      
      {/* 1. Top Header Toolbar */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Survey Statistics</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Detailed analytics of surveys, user completions, and point allocations.</p>
          {data?.filter?.start_date && data?.filter?.end_date && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50/50 text-blue-600 dark:bg-blue-955/20 dark:text-blue-450 border border-blue-100/40 dark:border-blue-900/30 shadow-sm">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3 text-blue-500/80 dark:text-blue-400/80" />
              Viewing data from <strong className="font-semibold">{data.filter.start_date}</strong> to <strong className="font-semibold">{data.filter.end_date}</strong>
            </span>
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-lg self-start border border-gray-200/50 dark:border-gray-700/50 shadow-inner">
          <button
            onClick={() => setViewMode('charts')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center ${
              viewMode === 'charts'
                ? 'bg-white text-gray-900 shadow dark:bg-gray-700 dark:text-white'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            <FontAwesomeIcon icon={faChartPie} className="mr-1.5" />
            Charts View
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition flex items-center ${
              viewMode === 'table'
                ? 'bg-white text-gray-900 shadow dark:bg-gray-700 dark:text-white'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
            }`}
          >
            <FontAwesomeIcon icon={faTable} className="mr-1.5" />
            Detailed Table
          </button>
        </div>
      </div>

      {/* 2. Filters Control Panel */}
      <form onSubmit={handleApplyFilter} className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          
          {/* Time Range Filter */}
          <div>
            <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faClock} className="mr-1 text-gray-400" />
              Time Range
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            >
              <option value="this_month">This Month</option>
              <option value="last_month">Last Month</option>
              <option value="last_3_month">Last 3 Months</option>
              <option value="last_6_month">Last 6 Months</option>
              <option value="last_9_month">Last 9 Months</option>
              <option value="last_ytd">Year to Date (YTD)</option>
              <option value="custom">Custom Date Range</option>
            </select>
          </div>

          {/* Project Code Filter */}
          <div>
            <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
              <FontAwesomeIcon icon={faFileAlt} className="mr-1 text-gray-400" />
              Project Code
            </label>
            <select
              value={projectCodeFilter}
              onChange={(e) => setProjectCodeFilter(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            >
              <option value="">All Projects</option>
              {projectCodes.map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>

          {/* Conditional Date Pickers for Custom range */}
          {filter === 'custom' && (
            <>
              {/* Start Date */}
              <div>
                <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-gray-400" />
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block mb-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 flex items-center">
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-1 text-gray-400" />
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-2 text-sm text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                  required
                />
              </div>
            </>
          )}

          {/* Segments Toggle & Actions Group */}
          <div className={`flex flex-wrap items-center justify-between gap-4 ${filter === 'custom' ? 'col-span-full' : 'col-span-1 sm:col-span-2'}`}>
            <label className="inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeSegments}
                onChange={(e) => setIncludeSegments(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              <span className="ms-3 text-xs font-semibold text-gray-600 dark:text-gray-400 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faLayerGroup} className="text-gray-400" />
                Include Segments
              </span>
            </label>

            <div className="flex gap-2 min-w-[150px] ml-auto">
              <button
                type="submit"
                className="activeBtn flex-1 text-white text-sm font-semibold py-2 px-4 rounded-lg flex items-center justify-center hover:opacity-95 shadow-sm transition"
              >
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                Apply
              </button>
              <button
                type="button"
                onClick={handleResetFilter}
                className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm font-semibold py-2 px-3 rounded-lg flex items-center justify-center transition shadow-sm"
                title="Reset Filters"
              >
                <FontAwesomeIcon icon={faUndo} />
              </button>
            </div>
          </div>

        </div>
      </form>

      {/* 3. KPI Summary Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        
        {/* Card 1: Surveys Count */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-indigo-500 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Total Survey Projects</p>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white">{formatNumber(totalSurveys)}</h3>
              <p className="text-xs text-indigo-500 mt-2 font-medium flex items-center gap-1">
                Currently listed matching criteria
              </p>
            </div>
            <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-500 rounded-lg">
              <FontAwesomeIcon icon={faFileAlt} className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 2: Completed Users Page */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-emerald-500 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Completed Users (Page)</p>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white">{formatNumber(totalUserCountPage)}</h3>
              <p className="text-xs text-emerald-500 mt-2 font-medium flex items-center gap-1">
                Summed across listed surveys
              </p>
            </div>
            <div className="p-2.5 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-500 rounded-lg">
              <FontAwesomeIcon icon={faUsers} className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Card 3: Points Transacted Page */}
        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm border-l-4 border-blue-500 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Total Points (Page)</p>
              <h3 className="text-2xl font-black text-gray-900 dark:text-white">{formatPoints(totalPointsPage)}</h3>
              <p className="text-xs text-blue-500 mt-2 font-medium flex items-center gap-1">
                <FontAwesomeIcon icon={faCoins} />
                Allocated reward value listed
              </p>
            </div>
            <div className="p-2.5 bg-blue-50 dark:bg-blue-950/40 text-blue-500 rounded-lg">
              <FontAwesomeIcon icon={faCoins} className="w-5 h-5" />
            </div>
          </div>
        </div>

      </div>

      {/* Loading indicator for filter changes */}
      {loading && (
        <div className="w-full py-4 flex justify-center mb-4">
          <Loader />
        </div>
      )}

      {/* 4. Display Content Section */}
      {viewMode === 'charts' ? (
        /* Charts View */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          
          {/* Points Chart */}
          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col">
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4">Points Distributed by Project</h4>
            <div className="w-full relative h-[300px] flex-1">
              {chartLabels.length > 0 ? (
                <Bar data={pointsChartData} options={chartOptions('points')} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm">
                  No chart data matches criteria.
                </div>
              )}
            </div>
          </div>

          {/* Users Chart */}
          <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm flex flex-col">
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4">Completions Count by Project</h4>
            <div className="w-full relative h-[300px] flex-1">
              {chartLabels.length > 0 ? (
                <Bar data={usersChartData} options={chartOptions('users')} />
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm">
                  No chart data matches criteria.
                </div>
              )}
            </div>
          </div>

        </div>
      ) : (
        /* Detailed Table View */
        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
          <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-4">Survey Code Details List</h4>
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700 text-gray-400 text-xs font-semibold uppercase tracking-wider">
                  <th className="py-3 px-4">Project Code</th>
                  <th className="py-3 px-4 text-center">Completed Users</th>
                  <th className="py-3 px-4 text-right">Total Points Distributed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {Survey.length > 0 ? (
                  Survey.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-55/40 dark:hover:bg-gray-700/30 transition">
                      <td className="py-3.5 px-4 font-bold tracking-wide">{item.project_code}</td>
                      <td className="py-3.5 px-4 text-center font-medium">{formatNumber(item.user_count)}</td>
                      <td className="py-3.5 px-4 text-right font-semibold text-blue-500">{formatPoints(item.total_points)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center py-8 text-gray-400 dark:text-gray-500">
                      No survey records match this query.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 5. Project Point Segments Detail Section */}
      {includeSegments && ProjectPointSegments && ProjectPointSegments.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-5 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FontAwesomeIcon icon={faLayerGroup} className="text-blue-500" />
            <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">Project Point Segments Breakdown</h4>
          </div>
          {renderSegmentsTable()}
        </div>
      )}

      {/* 6. Pagination Footer */}
      {pagination && pagination.count > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
          
          {/* Items Per Page Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                const newSize = Number(e.target.value);
                setPageSize(newSize);
                setPage(1); // Reset to page 1 on page size change
              }}
              className="bg-gray-50 dark:bg-gray-700 border border-gray-250 dark:border-gray-650 rounded-lg text-xs p-1 px-2 text-gray-800 dark:text-white focus:outline-none"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
            <span className="text-xs text-gray-450 dark:text-gray-400 ml-2">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, pagination.count)} of {pagination.count} records
            </span>
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page <= 1}
              className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Previous Page"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
              Page {page} of {pagination.no_page || 1}
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= (pagination.no_page || 1)}
              className="p-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              title="Next Page"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

        </div>
      )}

    </div>
  );
}

export default SurveyStats;